import { useEffect, useState } from "react";
import { Accordion, AccordionDetails, AccordionSummary } from "@mui/material";
import { styled } from "@mui/material/styles";
import { useStoreState, useStoreActions } from "easy-peasy";
import { animate, motion } from "motion/react";
import Logo from "../../images/teleforce-logo-mark.svg?react";
import AppleLogo from "../../images/ios.svg?react";
import AndroidLogo from "../../images/android.svg?react"; // You'll need to add this
import DotsIcon from "../../images/dots.svg?react";
import ShareIcon from "../../images/share.svg?react";
import AppLogo from "../../images/small-logo.svg?react";
import SafariLogo from "../../images/safari.svg?react";
import ChromeLogo from "../../images/chrome.svg?react";

const Install = () => {
  const [platform, setPlatform] = useState("unknown");

  useEffect(() => {
    const detectPlatform = () => {
      const userAgent = navigator.userAgent.toLowerCase();
      const isIOS = /iphone|ipad|ipod/.test(userAgent);
      const isAndroid = /android/.test(userAgent);
      const isChrome = /chrome|crios/.test(userAgent) && !/edg/.test(userAgent);
      const isSafari =
        /safari/.test(userAgent) && !/chrome|crios/.test(userAgent);

      if (isIOS) {
        if (isSafari) return "ios-safari";
        if (isChrome) return "ios-chrome";
      }

      if (isAndroid && isChrome) {
        return "android-chrome";
      }

      return "desktop";
    };

    setPlatform(detectPlatform());
  }, []);

  const renderInstructions = () => {
    switch (platform) {
      case "ios-safari":
        return (
          <>
            <StyledUser>
              <StyledAppleLogo /> <span>+</span> <SafariLogo />
            </StyledUser>

            <StyledOl>
              <StyledLi>
                Tap <DotsIcon /> at the bottom right
              </StyledLi>
              <StyledLi>
                Tap <ShareIcon /> icon at the top of the dialog
              </StyledLi>
              <StyledLi>
                Scroll down, choose <span>"Add to Home Screen"</span>
              </StyledLi>
              <StyledLi>
                Toggle on <span>"Open as Web app"</span> & tap{" "}
                <span>"Add"</span>
              </StyledLi>
              <StyledLi>
                Open from app icon <StyledAppLogo />
              </StyledLi>
            </StyledOl>
          </>
        );

      case "ios-chrome":
        return (
          <>
            <StyledUser>
              <StyledAppleLogo /> <span>+</span> <ChromeLogo />
            </StyledUser>

            <StyledOl>
              <StyledLi>
                Tap <ShareIcon /> icon at the top right
              </StyledLi>
              <StyledLi>
                Tap <DotsIcon /> at the bottom right
              </StyledLi>
              <StyledLi>
                Scroll down, choose <span>"Add to Home Screen"</span>
              </StyledLi>
              <StyledLi>
                Toggle on <span>"Open as Web app"</span> & tap{" "}
                <span>"Add"</span>
              </StyledLi>
              <StyledLi>
                Open from app icon <StyledAppLogo />
              </StyledLi>
            </StyledOl>
          </>
        );

      case "android-chrome":
        return (
          <>
            <StyledUser>
              <StyledAndroidLogo /> <span>+</span> <ChromeLogo />
            </StyledUser>
            <StyledOl>
              <StyledLi>
                Tap <DotsIcon /> at the top right
              </StyledLi>
              <StyledLi>
                Tap <span>"Add to Home screen"</span> or{" "}
                <span>"Install app"</span>
              </StyledLi>
              <StyledLi>
                Tap <span>"Add"</span> or <span>"Install"</span> to confirm
              </StyledLi>
              <StyledLi>
                Open from app icon <StyledAppLogo />
              </StyledLi>
            </StyledOl>
          </>
        );

      default:
        return (
          <StyledDescription>
            Please open this page on a mobile device to install the app.
          </StyledDescription>
        );
    }
  };

  return (
    <StyledContainer>
      <StyledLogo />
      <StyledGreeting>Install by adding to your home screen</StyledGreeting>
      <StyledDescription>
        To use this app on mobile, you need to add it to your home screen.
        Follow these instructions to do so.
      </StyledDescription>
      {renderInstructions()}
    </StyledContainer>
  );
};

export default Install;

const StyledUser = styled("div")({
  display: "flex",
  alignItems: "center",
  fontSize: 30,
  gap: ".75rem",
  marginBottom: "2rem"
});

const StyledAppleLogo = styled(AppleLogo)({
  width: 82,
  height: 38,
  display: "block",
  // marginBottom: "1rem",

  "& svg": {
    width: "100%",
    height: "auto"
  }
});

const StyledAndroidLogo = styled(AndroidLogo)({
  width: 82,
  height: 38,
  display: "block",
  marginBottom: "1rem",

  "& svg": {
    width: "100%",
    height: "auto"
  }
});

const StyledBrowserName = styled("div")({
  fontSize: 18,
  color: "var(--teleforce-50)",
  marginBottom: "2rem",
  fontWeight: 300
});

const StyledAppLogo = styled(AppLogo)({
  width: 28,
  height: 28,
  display: "inline-block",
  marginLeft: "0.5rem",

  "& svg": {
    width: "100%",
    height: "auto"
  }
});

const StyledContainer = styled("div")({
  margin: "0 auto",
  padding: "2rem",
  maxWidth: 1400
});

const StyledLogo = styled(Logo)({
  '& [fill]:not([fill="none"])': {
    fill: "var(--teleforce-50)"
  }
});

const StyledGreeting = styled("div")({
  fontSize: 38,
  lineHeight: 1.2,
  color: "var(--teleforce-50)",
  margin: "4rem 0 2rem 0",
  fontWeight: 200,
  borderBottom: "1px solid var(--teleforce-725)",
  marginBottom: "2rem",
  paddingBottom: "2rem"
});

const StyledDescription = styled("div")({
  fontSize: 20,
  color: "var(--teleforce-700)",
  margin: "0 0 2rem 0",
  fontWeight: 200,
  borderBottom: "1px solid var(--teleforce-725)",
  paddingBottom: "2rem"
});

const StyledOl = styled("ol")({
  fontSize: 20,
  color: "var(--teleforce-700)",
  margin: "0 0 2rem 0",
  fontWeight: 200,
  padding: 0,
  counterReset: "custom",
  listStyle: "none"
});

const StyledLi = styled("li")({
  fontSize: 16,
  color: "var(--teleforce-700)",
  margin: "0 0 1.25rem 0",
  fontWeight: 200,
  paddingLeft: "2.5rem",
  counterIncrement: "custom",
  position: "relative",
  display: "flex",
  alignItems: "center",

  "&::before": {
    content: "counter(custom)",
    color: "var(--teleforce-975)",
    fontWeight: 500,
    marginRight: "1rem",
    background: "var(--teleforce-50)",
    borderRadius: 50,
    width: 24,
    height: 24,
    display: "inline-block",
    textAlign: "center",
    lineHeight: "24px",
    position: "absolute",
    left: 0,
    top: 0
  },

  "& svg": {
    margin: "0 .65rem"
  },

  "& span": {
    color: "var(--teleforce-50)"
  }
});
